import { useState } from 'react';
import { ChevronDown, Flag, Plus, Users, X } from 'lucide-react';

import { PageHeader } from '@/components/PageHeader';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { QueryError } from '@/components/ui/QueryError';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { NO_HOUSE, NOT_ASSIGNED_LABEL } from '@/constants/houses';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { classesApi } from '@/features/institute/classes/api/classes.api';
import { useInstituteBranding } from '@/features/institute/store/branding.store';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ApiRequestError } from '@/lib/apiClient';
import type { House } from '@/types/house';
import { housesApi } from '../api/houses.api';
import { HouseCard, studentsLabel } from '../components/HouseCard';
import { HouseFormModal } from '../components/HouseFormModal';
import { HouseMembersPanel } from '../components/HouseMembersPanel';
import { cn } from '@/lib/utils';

/** Which house the members panel shows — the page's one filter. */
const SELECTION_DEFAULTS = { house: '' };

/**
 * The school's houses — Lotus, Lily, Rose, Sunflower — for sports, assemblies
 * and inter-house events.
 *
 * Everything happens on this page: houses are made and named here, choosing a
 * house lists its students below (`HouseMembersPanel`), and Assign students
 * adds more from any class and section (`AssignStudentsModal`). The Students
 * page shows and filters by house too, for the office working from the
 * register.
 *
 * An active house can only be suspended, and only once nobody is in it — the
 * dialog says so in red rather than letting a house in use be paused by
 * accident. A suspended house has its own section below the active ones, and
 * only there can it be deleted permanently.
 *
 * Read-only for anyone with STUDENT_VIEW but not STUDENT_MANAGE; the server
 * enforces the same split.
 */
export function HousesPage() {
  const permissions = useAuthStore((state) => state.user?.permissions);
  const canManage = Boolean(permissions?.includes('STUDENT_MANAGE'));

  const { data, isLoading, error, refetch } = useApiQuery(() => housesApi.list(), []);
  const [editing, setEditing] = useState<House | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [removing, setRemoving] = useState<House | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  /** The house awaiting the suspend confirmation. */
  const [suspending, setSuspending] = useState<House | null>(null);
  /** Uuid of the house whose status is being saved. */
  const [savingStatusOf, setSavingStatusOf] = useState<string | null>(null);
  /** Whose students the panel below shows. Null follows the first house. */
  // In the address, so Back from a student's record lands on the same house as
  // well as the panel's own filters. Blank means "the first house". An unknown
  // uuid falls through to the first house below, so it needs no allowed list.
  const [{ house: selectedHouse }, setSelectedHouse] = useUrlFilters(SELECTION_DEFAULTS);
  const selectedUuid = selectedHouse || null;
  const setSelectedUuid = (uuid: string | null) => setSelectedHouse({ house: uuid ?? '' });
  /** The house uuid whose assign mode should be activated inline in the table. */
  const [assignModeHouseUuid, setAssignModeHouseUuid] = useState<string | null>(null);
  /** Bumped after an assignment, so the open member list reads again. */
  const [reloadKey, setReloadKey] = useState(0);
  const [isSuspendedOpen, setIsSuspendedOpen] = useState(false);

  // The class and section filters. A college has none, so nothing is asked.
  const isCollege = useInstituteBranding((state) => state.branding?.institutionType === 'COLLEGE');
  const { data: classes } = useApiQuery(
    () => (isCollege ? Promise.resolve([]) : classesApi.list()),
    [isCollege],
  );

  const suspendingHasStudents = (suspending?.studentCount ?? 0) > 0;

  const houses = data?.houses ?? [];
  const activeHouses = houses.filter((house) => house.status === 'ACTIVE');
  const suspendedHouses = houses.filter((house) => house.status === 'SUSPENDED');
  // A removed or not-yet-chosen house falls back to the first one.
  const selected =
    selectedUuid === NO_HOUSE
      ? { uuid: NO_HOUSE, name: NOT_ASSIGNED_LABEL }
      : (houses.find((house) => house.uuid === selectedUuid) ?? houses[0] ?? null);

  function openForm(house: House | null) {
    setEditing(house);
    setIsFormOpen(true);
  }

  /**
   * Suspends or reactivates. Nobody moves either way, which is why reactivating
   * needs no confirmation and suspending (of an empty house) only a calm one.
   */
  async function changeStatus(house: House, status: House['status']) {
    setSavingStatusOf(house.uuid);
    try {
      await housesApi.setStatus(house.uuid, status);
      toast.success(
        status === 'SUSPENDED' ? `${house.name} suspended` : `${house.name} reactivated`,
        status === 'SUSPENDED'
          ? 'It takes no new students until you reactivate it.'
          : 'Students can be placed in it again.',
      );
      if (status === 'SUSPENDED') {
        setIsSuspendedOpen(true);
      }
      setSuspending(null);
      refetch();
    } catch (caught) {
      setSuspending(null);
      // Someone placed students in it since the page loaded: show the new count.
      if (caught instanceof ApiRequestError && caught.code === 'HOUSE_HAS_STUDENTS') refetch();
      toast.error(
        status === 'SUSPENDED'
          ? `Could not suspend ${house.name}`
          : `Could not reactivate ${house.name}`,
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setSavingStatusOf(null);
    }
  }

  async function confirmRemove() {
    if (!removing) return;
    const target = removing;
    setIsRemoving(true);
    try {
      await housesApi.remove(target.uuid);
      toast.success(
        `${target.name} deleted`,
        target.studentCount > 0
          ? `${studentsLabel(target.studentCount)} moved to ${NOT_ASSIGNED_LABEL}.`
          : undefined,
      );
      setRemoving(null);
      refetch();
    } catch (caught) {
      // The dialog closes first: a toast under an open dialog sits behind it.
      setRemoving(null);
      toast.error(
        `Could not delete ${target.name}`,
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsRemoving(false);
    }
  }

  return (
    <>
      <PageHeader
        
        title="Houses"
        description="School-wide houses for sports, morning assemblies, cultural events and inter-house competitions. Students from every class and section can be placed in them."
        actions={
          canManage || (data && data.unassignedCount > 0) ? (
            <div className="flex flex-wrap items-center gap-2.5">
              {data && data.unassignedCount > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedUuid(selected?.uuid === NO_HOUSE ? null : NO_HOUSE)}
                  className={cn(
                    'inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-xs sm:text-sm font-medium transition-all border cursor-pointer select-none',
                    selected?.uuid === NO_HOUSE
                      ? 'bg-amber-500 text-white border-amber-600 ring-2 ring-amber-500/20 shadow-xs'
                      : 'bg-amber-50 text-amber-900 border-amber-300 hover:bg-amber-100 hover:border-amber-400 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800',
                  )}
                  title={
                    selected?.uuid === NO_HOUSE
                      ? 'Cancel and return to houses'
                      : `${data.unassignedCount} students not placed in any house yet`
                  }
                >
                  <Users
                    className={cn(
                      'h-4 w-4 shrink-0',
                      selected?.uuid === NO_HOUSE ? 'text-white' : 'text-amber-800 dark:text-amber-400',
                    )}
                  />
                  <span>{studentsLabel(data.unassignedCount)} not assigned</span>
                  {selected?.uuid === NO_HOUSE ? (
                    <span
                      className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[red] hover:bg-[red]  text-white transition-colors ml-0.5"
                      title="Cancel unassigned view"
                      aria-label="Cancel unassigned view"
                    >
                      <X className="text-lg" />
                    </span>
                  ) : (
                    <span className="text-xs font-semibold ml-0.5 underline underline-offset-2 opacity-90">
                      View
                    </span>
                  )}
                </button>
              )}
              {canManage && (
                <button
                  type="button"
                  onClick={() => openForm(null)}
                  className="rounded-xl bg-indigo-600 tracking-wide hover:bg-indigo-700 text-white px-3.5 sm:px-4 py-2 text-xs sm:text-sm font-bold inline-flex items-center gap-1.5 sm:gap-2 transition-all cursor-pointer shadow-sm shadow-indigo-500/20 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                >
                  <Plus className="h-4 w-4 shrink-0" />
                  <span>Add house</span>
                </button>
              )}
            </div>
          ) : undefined
        }
      />

      {error && <QueryError title="Could not load the houses" error={error} onRetry={refetch} />}

      {isLoading && (
        <div className="card p-4">
          <SkeletonLines count={4} />
        </div>
      )}

      {data && houses.length === 0 && (
        <div className="card">
          <EmptyState
            icon={<Flag className="h-5 w-5" />}
            title="No houses yet"
            description={
              canManage
                ? 'Add your school’s houses — for example Lotus, Lily, Rose and Sunflower — then place students in them from the Students page.'
                : 'Your school office has not added any houses yet.'
            }
            action={
              canManage ? (
                <button
                  type="button"
                  onClick={() => openForm(null)}
                  className="rounded-xl bg-indigo-600 tracking-wide hover:bg-indigo-700 text-white px-3.5 sm:px-4 py-2 text-xs sm:text-sm font-bold inline-flex items-center gap-1.5 sm:gap-2 transition-all cursor-pointer shadow-sm shadow-indigo-500/20 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                >
                  <Plus className="h-4 w-4 shrink-0" />
                  <span>Add house</span>
                </button>
              ) : undefined
            }
          />
        </div>
      )}

      {data && houses.length > 0 && (
        <div className="flex flex-col gap-5">
          <ul
            className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            aria-label="Houses"
          >
            {activeHouses.map((house) => (
              <HouseCard
                key={house.uuid}
                house={house}
                isSelected={selected?.uuid === house.uuid}
                canManage={canManage}
                onView={() => setSelectedUuid(house.uuid)}
                onAssign={() => {
                  setSelectedUuid(house.uuid);
                  setAssignModeHouseUuid(house.uuid);
                }}
                onEdit={() => openForm(house)}
                onSuspend={() => setSuspending(house)}
                onReactivate={() => void changeStatus(house, 'ACTIVE')}
                onDelete={() => undefined}
              />
            ))}
          </ul>

          {suspendedHouses.length > 0 && (
            <section aria-labelledby="suspended-houses-heading" className="flex flex-col gap-3">
              <div>
                <button
                  type="button"
                  onClick={() => setIsSuspendedOpen((prev) => !prev)}
                  aria-expanded={isSuspendedOpen}
                  aria-controls="suspended-houses-content"
                  className="group inline-flex items-center gap-1.5 cursor-pointer select-none text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 rounded-md py-0.5"
                >
                  <h2 id="suspended-houses-heading" className="text-base font-semibold text-[red] flex items-center gap-1.5">
                    <span>Suspended houses ({suspendedHouses.length})</span>
                    <ChevronDown
                      className={cn(
                        'h-4 w-4 text-[red] transition-transform duration-200',
                        isSuspendedOpen && 'rotate-180',
                      )}
                      aria-hidden
                    />
                  </h2>
                </button>
              </div>

              {isSuspendedOpen && (
                <div id="suspended-houses-content" className="flex flex-col gap-3">
                  <p className="text-sm text-ink-2">
                    They take no new students. Reactivate one to use it again, or delete it
                    permanently if your school no longer needs it.
                  </p>
                  <ul
                    className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                    aria-label="Suspended houses"
                  >
                    {suspendedHouses.map((house) => (
                      <HouseCard
                        key={house.uuid}
                        house={house}
                        isSelected={selected?.uuid === house.uuid}
                        canManage={canManage}
                        onView={() => setSelectedUuid(house.uuid)}
                        onAssign={() => undefined}
                        onEdit={() => openForm(house)}
                        onSuspend={() => undefined}
                        onReactivate={() => void changeStatus(house, 'ACTIVE')}
                        onDelete={() => setRemoving(house)}
                      />
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}

          {selected && (
            <HouseMembersPanel
              houseUuid={selected.uuid}
              houseName={selected.name}
              houses={houses}
              onSelectHouse={(uuid) => setSelectedUuid(uuid)}
              classes={classes ?? []}
              hasPlacement={!isCollege}
              canManage={canManage}
              reloadKey={reloadKey}
              canAssign={
                !houses.some(
                  (house) => house.uuid === selected.uuid && house.status === 'SUSPENDED',
                )
              }
              assignModeHouseUuid={assignModeHouseUuid}
              onClearAssignMode={() => setAssignModeHouseUuid(null)}
              onChanged={() => {
                setReloadKey((k) => k + 1);
                refetch();
              }}
            />
          )}
        </div>
      )}

      <HouseFormModal
        house={editing}
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSaved={(saved) => {
          setIsFormOpen(false);
          toast.success(editing ? `${saved.name} saved` : `${saved.name} added`);
          refetch();
        }}
      />

      {/*
        Not the danger tone: nobody moves and it can be undone in one click. A
        house with students cannot be suspended at all — the server refuses it
        too — so the button stays disabled and the reason is shown in red.
      */}
      <ConfirmDialog
        isOpen={suspending !== null}
        title={`Suspend ${suspending?.name ?? 'this house'}?`}
        description="No new students can be placed in it until you reactivate it."
        confirmLabel="Suspend house"
        tone="danger"
        isLoading={suspending !== null && savingStatusOf === suspending.uuid}
        isConfirmDisabled={suspendingHasStudents}
        onConfirm={() => {
          if (suspending) void changeStatus(suspending, 'SUSPENDED');
        }}
        onCancel={() => setSuspending(null)}
      >
        {suspending && suspendingHasStudents &&  (
          <Alert tone="critical" title={`${suspending.name} has students assigned`}>
            {studentsLabel(suspending.studentCount)} {suspending.studentCount === 1 ? 'is' : 'are'}{' '}
            in this house. Move them to another house or to {NOT_ASSIGNED_LABEL} from View students
            before suspending it.
          </Alert>
        ) }
      </ConfirmDialog>

      {/*
        A Modal rather than ConfirmDialog: the latter's danger banner promises
        "nothing is deleted", which is the opposite of what this button says.
      */}
      <Modal
        isOpen={removing !== null}
        onClose={() => {
          if (!isRemoving) setRemoving(null);
        }}
        title={`Remove ${removing?.name ?? 'this house'} for good?`}
        description="It will no longer appear on the Houses page or as a choice anywhere a house is picked."
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setRemoving(null)} disabled={isRemoving}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => void confirmRemove()} isLoading={isRemoving}>
              Remove the house for good
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm font-semibold text-crit">This cannot be undone.</p>
          {removing && removing.studentCount > 0 && (
            <Alert tone="warning">
              {studentsLabel(removing.studentCount)} still in it will show as {NOT_ASSIGNED_LABEL}{' '}
              until they are placed in another house.
            </Alert>
          )}
        </div>
      </Modal>
    </>
  );
}
